//src/pages/EditarEmpresa.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import ModalSemPermissao from "../components/ModalSemPermissao";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  MapPin,
  Save,
  Search,
  ShieldCheck,
} from "lucide-react";

import "./EditarEmpresa.css";

function EditarEmpresa() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [consultandoCep, setConsultandoCep] = useState(false);

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  const podeEditarEmpresas =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "empresas" && p.pode_editar);

  useEffect(() => {
    axios
      .get(`/empresas/${id}`)
      .then((res) => setForm(res.data))
      .catch(() => alert("Erro ao carregar empresa"));
  }, [id]);

  if (!podeEditarEmpresas) {
    return <ModalSemPermissao onClose={() => (window.location.href = "/empresas")} />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buscarCEP = async () => {
    const cepLimpo = form.cep?.replace(/\D/g, "");

    if (!cepLimpo || cepLimpo.length !== 8) return;

    try {
      setConsultandoCep(true);

      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();

      if (!data.erro) {
        setForm((prev) => ({
          ...prev,
          logradouro: data.logradouro || "",
          bairro: data.bairro || "",
          cidade: data.localidade || "",
          estado: data.uf || "",
        }));
      }
    } catch (err) {
      console.error("Erro ao buscar CEP:", err);
    } finally {
      setConsultandoCep(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSalvando(true);

      await axios.put(`/empresas/${id}`, form);

      alert("Empresa atualizada com sucesso!");
      navigate("/empresas");
    } catch (err) {
      console.error("Erro ao atualizar empresa:", err);
      alert("Erro ao atualizar empresa.");
    } finally {
      setSalvando(false);
    }
  };

  if (!form) {
    return (
      <div className="editarEmpresaAdmin-state">
        <Building2 size={26} />
        <p>Carregando dados da empresa...</p>
      </div>
    );
  }

  return (
    <div className="editarEmpresaAdmin-page">
      <div className="editarEmpresaAdmin-topbar">
        <div>
          <span className="editarEmpresaAdmin-kicker">Área administrativa</span>
          <h2>Editar empresa</h2>
          <p>
            Atualize os dados cadastrais, endereço e status da empresa no
            programa Tikpontos.
          </p>
        </div>

        <button
          type="button"
          className="editarEmpresaAdmin-btn secondary"
          onClick={() => navigate("/empresas")}
        >
          <ArrowLeft size={16} />
          Voltar para empresas
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editarEmpresaAdmin-card">
        <section className="editarEmpresaAdmin-section">
          <div className="editarEmpresaAdmin-sectionHeader">
            <div className="editarEmpresaAdmin-icon">
              <Building2 size={20} />
            </div>

            <div>
              <h3>Dados da empresa</h3>
              <span>O CNPJ fica bloqueado para preservar o vínculo cadastral.</span>
            </div>
          </div>

          <div className="editarEmpresaAdmin-grid">
            <div className="editarEmpresaAdmin-field span4">
              <label>CNPJ</label>
              <input name="cnpj" value={form.cnpj || ""} disabled />
            </div>

            <div className="editarEmpresaAdmin-field span8">
              <label>Razão Social</label>
              <input
                name="razao_social"
                value={form.razao_social || ""}
                onChange={handleChange}
                placeholder="Razão social da empresa"
              />
            </div>
          </div>
        </section>

        <section className="editarEmpresaAdmin-section">
          <div className="editarEmpresaAdmin-sectionHeader">
            <div className="editarEmpresaAdmin-icon teal">
              <MapPin size={20} />
            </div>

            <div>
              <h3>Endereço</h3>
              <span>Preencha o CEP para buscar automaticamente o endereço.</span>
            </div>
          </div>

          <div className="editarEmpresaAdmin-grid">
            <div className="editarEmpresaAdmin-field span3">
              <label>CEP</label>

              <div className="editarEmpresaAdmin-inputAction">
                <input
                  name="cep"
                  value={form.cep || ""}
                  onChange={handleChange}
                  onBlur={buscarCEP}
                  placeholder="00000-000"
                />

                <button
                  type="button"
                  onClick={buscarCEP}
                  disabled={consultandoCep}
                  title="Buscar CEP"
                >
                  <Search size={15} />
                </button>
              </div>

              <small>{consultandoCep ? "Buscando CEP..." : "Consulta via CEP."}</small>
            </div>

            <div className="editarEmpresaAdmin-field span9">
              <label>Logradouro</label>
              <input
                name="logradouro"
                value={form.logradouro || ""}
                onChange={handleChange}
                placeholder="Rua, avenida, praça..."
              />
            </div>

            <div className="editarEmpresaAdmin-field span3">
              <label>Número</label>
              <input
                name="numero"
                value={form.numero || ""}
                onChange={handleChange}
                placeholder="Número"
              />
            </div>

            <div className="editarEmpresaAdmin-field span9">
              <label>Complemento</label>
              <input
                name="complemento"
                value={form.complemento || ""}
                onChange={handleChange}
                placeholder="Sala, conjunto, bloco..."
              />
            </div>

            <div className="editarEmpresaAdmin-field span5">
              <label>Bairro</label>
              <input
                name="bairro"
                value={form.bairro || ""}
                onChange={handleChange}
                placeholder="Bairro"
              />
            </div>

            <div className="editarEmpresaAdmin-field span5">
              <label>Cidade</label>
              <input
                name="cidade"
                value={form.cidade || ""}
                onChange={handleChange}
                placeholder="Cidade"
              />
            </div>

            <div className="editarEmpresaAdmin-field span2">
              <label>UF</label>
              <input
                name="estado"
                value={form.estado || ""}
                onChange={handleChange}
                maxLength={2}
                placeholder="UF"
              />
            </div>
          </div>
        </section>

        <section className="editarEmpresaAdmin-section">
          <div className="editarEmpresaAdmin-sectionHeader">
            <div className="editarEmpresaAdmin-icon orange">
              <ShieldCheck size={20} />
            </div>

            <div>
              <h3>Status</h3>
              <span>Defina se a empresa permanece ativa no sistema.</span>
            </div>
          </div>

          <div className="editarEmpresaAdmin-grid">
            <div className="editarEmpresaAdmin-field span4">
              <label>Status</label>
              <select
                name="ativa"
                value={form.ativa}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    ativa: parseInt(e.target.value, 10),
                  }))
                }
              >
                <option value={1}>Ativa</option>
                <option value={0}>Inativa</option>
              </select>
            </div>
          </div>
        </section>

        <div className="editarEmpresaAdmin-actions">
          <button
            type="button"
            className="editarEmpresaAdmin-btn secondary"
            onClick={() => navigate("/empresas")}
          >
            <ArrowLeft size={16} />
            Cancelar
          </button>

          <button
            type="submit"
            className="editarEmpresaAdmin-btn primary"
            disabled={salvando}
          >
            {salvando ? (
              <>
                <CheckCircle2 size={16} />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                Salvar alterações
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditarEmpresa;